<script setup lang="ts">
import { computed } from "vue";
import {
  NConfigProvider,
  NMessageProvider,
  NDialogProvider,
  NNotificationProvider,
  NLoadingBarProvider,
  lightTheme,
  darkTheme,
  type GlobalThemeOverrides,
} from "naive-ui";
import { RouterView } from "vue-router";

import { useTheme } from "@/composables/useTheme";

/**
 * Map every --tl-* design token onto Naive UI's theme overrides so
 * Naive UI components (NButton, NInput, etc.) speak the same brand
 * colour as the Tailwind layout. Without this, Naive UI defaults to
 * its own blue and breaks the design.
 *
 * Naive UI uses seemly/rgba internally to derive opacity variants from
 * these colors, so we must pass literal hex values — CSS var() references
 * are not parseable by seemly and would throw at runtime.
 *
 * Because the dark theme uses a different primary (#f59e0b vs #c25e0b),
 * we swap the overrides based on the resolved isDark flag.
 */
const lightOverrides: GlobalThemeOverrides = {
  common: {
    primaryColor: "#c25e0b",
    primaryColorHover: "#c25e0b",
    primaryColorPressed: "#9a3412",
    primaryColorSuppl: "#c25e0b",
    successColor: "#15803d",
    warningColor: "#a16207",
    errorColor: "#b91c1c",
    infoColor: "#0369a1",
    borderRadius: "8px",
    fontFamily:
      'Inter, "Noto Sans SC", "PingFang SC", "Microsoft YaHei", system-ui, sans-serif',
  },
};

const darkOverrides: GlobalThemeOverrides = {
  common: {
    primaryColor: "#f59e0b",
    primaryColorHover: "#f59e0b",
    primaryColorPressed: "#d97706",
    primaryColorSuppl: "#f59e0b",
    successColor: "#15803d",
    warningColor: "#a16207",
    errorColor: "#b91c1c",
    infoColor: "#0369a1",
    borderRadius: "8px",
    fontFamily:
      'Inter, "Noto Sans SC", "PingFang SC", "Microsoft YaHei", system-ui, sans-serif',
  },
};

const { isDark } = useTheme();

const naiveTheme = computed(() => (isDark.value ? darkTheme : lightTheme));
const themeOverrides = computed(() => (isDark.value ? darkOverrides : lightOverrides));
</script>

<template>
  <NConfigProvider :theme="naiveTheme" :theme-overrides="themeOverrides">
    <NLoadingBarProvider>
      <NMessageProvider>
        <NDialogProvider>
          <NNotificationProvider>
            <RouterView />
          </NNotificationProvider>
        </NDialogProvider>
      </NMessageProvider>
    </NLoadingBarProvider>
  </NConfigProvider>
</template>
